// FRONT-3142: opening a chat window must land at the very bottom.
//
// The one-shot "snap to bottom" used to run at first commit via a sentinel's
// scrollIntoView. Two things left the reader a little above the floor: the
// sentinel sits INSIDE the column's own bottom padding, so aligning it to the
// scroller's edge never reaches the true end, and content that settles after
// that first scroll (markdown, images, fonts, footers) grows the column with
// nothing to re-scroll. The fix targets the scroller's real maximum scroll
// position and re-applies it while the window is still settling.

/** How long after open the window keeps re-pinning to the floor as it grows. */
export const OPEN_SETTLE_MS = 2000;

/** The scrollTop that shows the very end of the scroller's content. */
export function bottomScrollTop(el: { scrollHeight: number; clientHeight: number }): number {
  return Math.max(0, el.scrollHeight - el.clientHeight);
}

/**
 * Whether a content-growth notification should re-pin the reader to the floor.
 * Only while the open settle window is live AND the reader is still pinned
 * (has not scrolled away); afterwards growth is the streaming effects' job.
 */
export function shouldRepinOnGrowth(opts: {
  now: number;
  settleUntil: number;
  pinned: boolean;
}): boolean {
  return opts.pinned && opts.now < opts.settleUntil;
}

/** What the scroller search reads from each ancestor, nearest first. */
export interface ScrollAncestor {
  overflowY: string;
  position: string;
  scrollHeight: number;
  clientHeight: number;
}

/**
 * Index of the ancestor that scrolls the conversation, or -1 when none does.
 * Prefers one that already overflows, else the nearest with a scrollable
 * overflow-y style. The search stops at the first `position: fixed` ancestor:
 * a chat window is fixed over the page that mounted it, and scrolling that
 * page never moves the window, so picking it (a short chat in a window over a
 * long list) or letting `scrollIntoView` walk into it is what scrolled the
 * Chats list behind an open window to its end.
 */
export function pickScroller(ancestors: ScrollAncestor[]): number {
  let fallback = -1;
  for (let i = 0; i < ancestors.length; i++) {
    const a = ancestors[i];
    if (a.overflowY === 'auto' || a.overflowY === 'scroll') {
      if (fallback === -1) fallback = i;
      if (a.scrollHeight > a.clientHeight) return i;
    }
    if (a.position === 'fixed') break;
  }
  return fallback;
}
